import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidV4 } from 'uuid';
import classNames from 'classnames';
import { useImpressionContainer, useImpressionItem } from '@fiverr-private/impressionable';
import { Text } from '@fiverr-private/typography';
import { getContext } from '@fiverr-private/fiverr_context';
import { ExpertCardContextProvider, useExpertCardContext } from '../../contexts';
import ExpertisesList from '../ExpertisesList';
import { CARD_VARIANT, CTAS } from '../../shared/constants';
import { metric } from '../../utils/biEvents/stats/metric';
import useHideGigObserver from '../../hooks/useHideGigObserver';
import { reportAdBlockerDetection } from '../../hooks/useHideGigObserver/adBlockerDetection';
import {
    fetchOnlineStatus,
    getBiEnrichment,
    getDisplayPrice,
    getFreelancerUrl,
    handleCardHover,
    handleExpertCardClick,
    hasOfferConsultationGig,
} from './utils';
import { getImpressionEnrichment, getName, onExpertCardImpression } from './impression';
import SellerDetails from './SellerDetails';
import cardPropTypes, { clickableAreaPropsTypes } from './types';
import Footer from './Footer';
import VisualArea from './VisualArea';
import { EmployeesBigList } from './Employees';

import './index.scss';

export const ClickableArea = ({ children, setIsHovered, clickHandlerArgs }) => {
    const ref = useRef(null);
    const { auctionId, promotedGigId } = useExpertCardContext();
    const { isMobile } = getContext();
    return (
        <div
            className="clickable-section"
            ref={ref}
            onClick={
                isMobile
                    ? null
                    : handleExpertCardClick({
                          ...clickHandlerArgs,
                          auctionId,
                          promotedGigId,
                          ref,
                          listenOnAllElements: false,
                      })
            }
            onMouseOver={handleCardHover(ref, true, setIsHovered)}
            onMouseOut={handleCardHover(ref, false, setIsHovered)}
        >
            {children}
        </div>
    );
};

/**
 * Expert card component that leads to an expert page.
 * Includes the seller and gig details.
 */
const ExpertCard = ({
    seller = {},
    gigs,
    expertises,
    highlights,
    highlightsData,
    rating,
    completedOrdersCount,
    notableClients,
    position,
    cardVariant,
    displayOptions = {},
    cardContainerClassName,
    forceSmallCard = false,
    impressionId: cardImpressionId = uuidV4(),
    currency,
    componentName = getName(cardVariant),
    onImpression,
    renderFooterContent,
    source = 'expert_card',
    contextReferrer,
    disableSwipeMode = false,
    lazyLoadAssets = false,
    saveToListProps = {},
    mutateUrl = (url) => url,
    headerCustomButtons,
    footerCustomButtons,
    showHighlightsInSmallCard = false,
    showHourlyPrice = false,
    onClick,
    onSellerDetailsClick,
    pageName,
    isPromoted,
    auctionId,
    promotedGigId,
    additionalInfo,
    isSelectable = false,
    openCtaInNewTab = false,
}) => {
    const [isSellerOnline, setIsSellerOnline] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const priceInUsd = getDisplayPrice(gigs);
    const { showJobTitle = true, mainCta = CTAS.SEE_PROFILE } = displayOptions;
    const { agency } = seller;
    const isAgencyCard = !!agency;

    const impressionEnrichment = getImpressionEnrichment({
        rating: rating?.averageValuation,
        completedOrdersCount,
        isSellerOnline,
        expertises,
        highlights,
        highlightsData,
        seller,
        notableClients,
        cardVariant,
        priceInUsd,
        currency,
        gigId: gigs?.[0]?.id,
        gigEngine: gigs?.[0]?.engine,
        componentName,
        hourlyCard: showHourlyPrice,
        auctionId,
    });

    const [expertCardRef, containerId] = useImpressionContainer({ selfId: cardImpressionId });

    useHideGigObserver({
        targetRef: expertCardRef,
        callback: useCallback(() => {
            const routePath = window.bigQueryEnrichment?.page?.name;
            const platform = window.bigQueryEnrichment?.platform;
            metric.count(`expert_gig_card.display_none.applied.${platform}.${routePath}`);
            reportAdBlockerDetection(seller?.id, gigs?.[0]?.id, auctionId);
        }, [auctionId, gigs, seller?.id]),
    });

    const [, impressionId] = useImpressionItem({
        selfId: containerId,
        selfRef: expertCardRef,
        enrichment: impressionEnrichment,
        sendImpressionToBQ: false,
        intersectionThreshold: 0.1,
        onImpression: onImpression ?? ((...args) => onExpertCardImpression(pageName, componentName, ...args)),
    });

    const biEnrichment = getBiEnrichment({ seller, impressionId });

    useEffect(() => {
        const setOnlineStatus = () => {
            // eslint-disable-next-line promise/catch-or-return
            fetchOnlineStatus(seller.username)
                .then((isOnline) => setIsSellerOnline(isOnline))
                .catch(() => true);
        };

        setOnlineStatus();
    }, [seller]);

    let freelancerUrl = getFreelancerUrl({
        expertises,
        gigs,
        seller,
        impressionId,
        source,
        agencySlug: agency?.slug,
        isExperiential: cardVariant === CARD_VARIANT.VISUAL,
        contextReferrer,
    });

    freelancerUrl = mutateUrl(freelancerUrl);

    const clickHandlerArgs = { freelancerUrl, impressionId, source, seller, componentName, onClick };
    const { isMobile } = getContext();
    const cardClassNames = classNames('listings-expert-card-container co-text-darker', cardContainerClassName, {
        'small-card': forceSmallCard,
        'shadow-on-hover': !isMobile && !isSelectable,
        'p-24': !isMobile && !isSelectable,
        'p-20': isMobile,
        'with-border': !isSelectable,
    });
    const offerConsultation = hasOfferConsultationGig(gigs);

    const handleCardClickedForMobile = () => {
        if (!isMobile) {
            return null;
        }
        return handleExpertCardClick({ ...clickHandlerArgs, ref: expertCardRef, listenOnAllElements: true });
    };

    return (
        <ExpertCardContextProvider
            cardPosition={position}
            seller={seller}
            freelancerUrl={freelancerUrl}
            cardImpressionId={impressionId}
            componentName={componentName}
            rating={rating}
            isAgencyCard={isAgencyCard}
            saveToListProps={saveToListProps}
            offerConsultation={offerConsultation}
            gigs={gigs}
            displayOptions={displayOptions}
            headerCustomButtons={headerCustomButtons}
            pageName={pageName}
            isPromoted={isPromoted}
            auctionId={auctionId}
            promotedGigId={promotedGigId}
            openCtaInNewTab={openCtaInNewTab}
        >
            <div className={cardClassNames} ref={expertCardRef} onClick={handleCardClickedForMobile()}>
                <ClickableArea setIsHovered={setIsHovered} clickHandlerArgs={clickHandlerArgs}>
                    <SellerDetails
                        source={source}
                        isSellerOnline={isSellerOnline}
                        isCardHovered={isHovered}
                        showJobTitle={showJobTitle}
                        onSellerDetailsClick={onSellerDetailsClick}
                    />
                </ClickableArea>
                {isAgencyCard ? <EmployeesBigList employees={agency?.members} /> : null}
                <div className="expert-card-body">
                    <ExpertisesList
                        expertises={seller.allExpertises}
                        hasHourlyRate={!!seller?.hourlyRateInCents}
                        forceSmallCard={forceSmallCard}
                        biEnrichment={biEnrichment}
                    />
                </div>
                {additionalInfo && <Text marginTop="4">{additionalInfo}</Text>}
                <VisualArea
                    cardVariant={cardVariant}
                    clickHandlerArgs={clickHandlerArgs}
                    biEnrichment={biEnrichment}
                    setIsHovered={setIsHovered}
                    isHovered={isHovered}
                    gigs={gigs}
                    currency={currency}
                    disableSwipeMode={disableSwipeMode}
                    lazyLoadAssets={lazyLoadAssets}
                />
                <ClickableArea setIsHovered={setIsHovered} clickHandlerArgs={clickHandlerArgs}>
                    <Footer
                        priceInUSD={priceInUsd}
                        highlights={highlights}
                        highlightsData={highlightsData}
                        cardVariant={cardVariant}
                        {...displayOptions}
                        currency={currency}
                        mainCta={mainCta}
                        seller={seller}
                        renderContent={renderFooterContent}
                        source={source}
                        footerCustomButtons={footerCustomButtons}
                        showHighlightsInSmallCard={showHighlightsInSmallCard}
                        hourlyRateInCents={seller?.hourlyRateInCents}
                        showHourlyPrice={showHourlyPrice}
                    />
                </ClickableArea>
            </div>
        </ExpertCardContextProvider>
    );
};

ExpertCard.propTypes = cardPropTypes;
ClickableArea.propTypes = clickableAreaPropsTypes;

export default ExpertCard;
